import { useState, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useGame } from '../state/store'
import { fmt } from '../engine/decimal'
import { nextLevelCost, letterOutput, type LetterState } from '../engine/formulas'
import { LETTERS, LETTERS_BY_ID } from '../config/letters'
import { GLOBAL, type Rarity } from '../config/global'
import { MasteryCard } from './MasteryCard'

const RARITY_CLASS: Record<Rarity, string> = {
  Common: 'text-rarity-common border-rarity-common/40',
  Uncommon: 'text-rarity-uncommon border-rarity-uncommon/40',
  Rare: 'text-rarity-rare border-rarity-rare/40',
  Legend: 'text-rarity-legend border-rarity-legend/40',
}

function visibleCount(letters: LetterState[]): number {
  let owned = 0
  for (const l of letters) if (l.level > 0) owned++
  return Math.min(LETTERS.length, owned + 2)
}

type BuyMode = 'x1' | 'x10' | 'MAX'

export function LetterShop() {
  const letters = useGame((s) => s.letters)
  const ink = useGame((s) => s.ink)
  const mastery = useGame((s) => s.mastery)
  const buyLetter = useGame((s) => s.buyLetter)
  const levelUp = useGame((s) => s.levelUp)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [buyMode, setBuyMode] = useState<BuyMode>('x1')
  const holdTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  const max = visibleCount(letters)

  const cycleBuyMode = () => {
    setBuyMode((prev) => (prev === 'x1' ? 'x10' : prev === 'x10' ? 'MAX' : 'x1'))
  }

  // Hold-to-repeat for level up
  const startHold = useCallback((id: number) => {
    levelUp(id)
    holdTimer.current = setInterval(() => levelUp(id), 150)
  }, [levelUp])

  const stopHold = useCallback(() => {
    if (holdTimer.current) {
      clearInterval(holdTimer.current)
      holdTimer.current = null
    }
  }, [])

  return (
    <section className="rounded-lg border border-ink-border bg-ink-panel p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-ink-teal">
          Letter Shop
        </h2>
        <button
          onClick={cycleBuyMode}
          className="rounded border border-ink-border px-2 py-0.5 text-[10px] text-ink-muted hover:border-ink-tealdim hover:text-ink-teal"
        >
          {buyMode}
        </button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {letters.slice(0, max).map((ls) => {
          const def = LETTERS_BY_ID[ls.id]
          const owned = ls.level > 0
          const cost = nextLevelCost(def, ls.level)
          const affordable = ink.gte(cost)
          const out = letterOutput(ls)
          const preActive = owned && ls.level < GLOBAL.passiveUnlockLevel
          const m = mastery[ls.id]
          const milestoneCount = m
            ? [m.milestoneLexicographer, m.milestoneLengthy, m.milestoneRaritySeeker, m.milestoneChainLink, m.milestoneSwift].filter(Boolean).length
            : 0
          const isExpanded = expandedId === ls.id

          return (
            <motion.div
              key={ls.id}
              className="relative"
              animate={affordable && owned ? { boxShadow: ['0 0 0px rgba(45,212,191,0)', '0 0 8px rgba(45,212,191,0.15)', '0 0 0px rgba(45,212,191,0)'] } : {}}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="flex items-center gap-1">
              <button
                disabled={!affordable && !owned}
                onClick={() => {
                  if (owned) {
                    setExpandedId(isExpanded ? null : ls.id)
                  } else {
                    buyLetter(ls.id)
                  }
                }}
                className={`flex-1 flex items-center gap-3 rounded-md border bg-ink-bg p-3 text-left transition ${
                  affordable || owned
                    ? 'border-ink-tealdim hover:shadow-glow'
                    : 'border-ink-border opacity-60'
                }`}
              >
                <span
                  className={`chip h-11 w-11 text-xl ${RARITY_CLASS[def.rarity]} ${
                    ls.capital ? 'font-extrabold' : ''
                  }`}
                >
                  [{ls.capital ? def.letter : def.letter.toLowerCase()}]
                </span>
                <span className="flex-1">
                  <span className="flex justify-between text-sm">
                    <span className="text-ink-text">
                      {def.letter}
                      {owned && (
                        <span className="text-ink-muted"> · Lv{ls.level}</span>
                      )}
                    </span>
                    <span className="text-[10px] uppercase text-ink-muted">
                      {def.rarity}
                    </span>
                  </span>
                  <span className="mt-0.5 flex justify-between text-xs">
                    <span className="text-ink-teal">
                      {owned ? `${fmt(out)}/s` : 'Buy'}
                    </span>
                    <span
                      className={affordable ? 'text-ink-text' : 'text-ink-muted'}
                    >
                      {fmt(cost)} Ink
                    </span>
                  </span>
                  {preActive && (
                    <span className="mt-0.5 block text-[10px] text-ink-muted">
                      passive at Lv{GLOBAL.passiveUnlockLevel}
                    </span>
                  )}
                  {def.trait && owned && (
                    <span className="mt-0.5 block text-[10px] text-ink-muted">
                      trait: {def.trait}
                    </span>
                  )}
                  {owned && !ls.capital && (
                    <span className="mt-1 flex gap-0.5">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span
                          key={i}
                          className={`inline-block h-1.5 w-1.5 rounded-full ${
                            i < milestoneCount ? 'bg-ink-teal' : 'bg-ink-border'
                          }`}
                        />
                      ))}
                    </span>
                  )}
                </span>
              </button>
              {owned && (
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onMouseDown={() => startHold(ls.id)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(ls.id)}
                  onTouchEnd={stopHold}
                  disabled={!affordable}
                  className="shrink-0 rounded border border-ink-tealdim px-1.5 py-1 text-[11px] font-bold text-ink-teal hover:shadow-glow disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ↑
                </motion.button>
              )}
            </div>
              {isExpanded && owned && (
                <div className="absolute left-0 right-0 top-full z-10">
                  <MasteryCard letter={ls} />
                </div>
              )}
            </motion.div>
          )
        })}
      </div>
    </section>
  )
}
